import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/button";
import {
  Dialog,
  DialogCloseButton,
  DialogFooter,
  DialogTitle,
} from "@/components/dialog";
import { Modal } from "@/components/modal";

import type { SizeItem } from "./size-presets";

import {
  FILE_TYPE_PRESETS,
  sameFileType,
  sameSize,
  SIZE_PRESETS,
  sizeIcon,
} from "./size-presets";

type EditSizeModalProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSave: (deliverables: Deliverables) => void;
  value: Deliverables;
};

export type Deliverables = {
  fileSizes: SizeItem[];
  fileTypes: string[];
};

/** Parse a free-typed "1080x1080" / "1080 × 1920" into a normalized size. */
function parseCustomSize(raw: string): null | SizeItem {
  const match = raw.match(/(\d+)\s*[x×]\s*(\d+)/i);

  if (!match) return null;

  return { label: "Custom", size: `${match[1]}×${match[2]}px` };
}

function withoutPresets(sizes: SizeItem[]): SizeItem[] {
  return sizes.filter(
    (item) => !SIZE_PRESETS.some((preset) => sameSize(preset, item)),
  );
}

export function EditSizeModal({
  isOpen,
  onOpenChange,
  onSave,
  value,
}: EditSizeModalProps) {
  const [selected, setSelected] = useState<SizeItem[]>(value.fileSizes);
  const [customSizes, setCustomSizes] = useState<SizeItem[]>([]);
  const [custom, setCustom] = useState("");
  const [customError, setCustomError] = useState<null | string>(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(value.fileTypes);
  const [customType, setCustomType] = useState("");

  // Reseed local selection whenever the modal opens.
  useEffect(() => {
    if (isOpen) {
      setSelected(value.fileSizes);
      setCustomSizes(withoutPresets(value.fileSizes));
      setCustom("");
      setCustomError(null);
      setSelectedTypes(value.fileTypes);
      setCustomType("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isSelected = (item: SizeItem) =>
    selected.some((current) => sameSize(current, item));

  const toggle = (item: SizeItem) => {
    setSelected((prev) =>
      prev.some((current) => sameSize(current, item))
        ? prev.filter((current) => !sameSize(current, item))
        : [...prev, item],
    );
  };

  const cards: SizeItem[] = [...SIZE_PRESETS, ...customSizes];

  const addCustom = () => {
    if (!custom.trim()) return;

    const parsed = parseCustomSize(custom);

    if (!parsed) {
      setCustomError("Enter a size as width x height, like 1080x1080");

      return;
    }
    if (isSelected(parsed)) {
      setCustomError("That size is already added.");

      return;
    }

    const existing = cards.find((card) => sameSize(card, parsed));

    if (existing) {
      toggle(existing);
    } else {
      setCustomSizes((prev) => [...prev, parsed]);
      setSelected((prev) => [...prev, parsed]);
    }
    setCustom("");
  };

  const isTypeSelected = (type: string) =>
    selectedTypes.some((current) => sameFileType(current, type));

  const toggleType = (type: string) => {
    setSelectedTypes((prev) =>
      prev.some((current) => sameFileType(current, type))
        ? prev.filter((current) => !sameFileType(current, type))
        : [...prev, type],
    );
  };

  const addCustomType = () => {
    const type = customType.trim();

    if (!type) return;
    if (!isTypeSelected(type)) setSelectedTypes((prev) => [...prev, type]);
    setCustomType("");
  };

  const typeOptions = [
    ...FILE_TYPE_PRESETS,
    ...selectedTypes.filter(
      (type) => !FILE_TYPE_PRESETS.some((preset) => sameFileType(preset, type)),
    ),
  ];

  return (
    <Modal
      classNames={{ modal: "w-[900px] max-w-[calc(100vw-48px)]" }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <Dialog className="flex max-h-[90vh] flex-col">
        <DialogCloseButton />

        <div className="px-6 pb-4 pr-[60px] pt-6">
          <DialogTitle className="text-xl font-semibold text-text-primary">
            Edit deliverables
          </DialogTitle>
          <p className="mt-2 max-w-[560px] text-sm text-text-tertiary">
            Choose a preset size or enter a custom size deliverable in WxH
            format and press enter
          </p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6">
          <div className="flex items-center gap-2 rounded-lg border border-border-primary bg-bg-primary px-3.5 py-3">
            <input
              aria-invalid={customError !== null}
              className="min-w-0 flex-1 bg-transparent text-sm text-text-primary outline-hidden placeholder:text-text-placeholder"
              onChange={(event) => {
                setCustom(event.target.value);
                setCustomError(null);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  addCustom();
                }
              }}
              placeholder="Select a format"
              value={custom}
            />
          </div>
          {customError ? (
            <p className="mt-2 text-sm text-text-error-primary" role="alert">
              {customError}
            </p>
          ) : null}

          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {cards.map((item, i) => {
              const Icon = sizeIcon(item.size);
              const active = isSelected(item);

              return (
                <button
                  aria-pressed={active}
                  className={twMerge(
                    "flex min-w-0 flex-col items-center gap-2 rounded-xl border border-border-secondary bg-bg-secondary px-4 py-5 text-center outline-hidden transition-colors hover:border-border-brand",
                    active && "border-border-brand bg-utility-brand-50",
                  )}
                  key={`${item.label}-${item.size}-${i}`}
                  onClick={() => toggle(item)}
                  type="button"
                >
                  <Icon className="size-6 shrink-0 text-fg-brand-primary" />
                  <span className="max-w-full break-words text-md font-semibold text-text-primary">
                    {item.label}
                  </span>
                  <span className="max-w-full break-all text-sm text-text-tertiary">
                    {item.size}
                  </span>
                </button>
              );
            })}
          </div>

          <h3 className="mt-8 text-md font-semibold text-text-primary">
            File types
          </h3>
          <p className="mt-1 text-sm text-text-tertiary">
            Pick the formats you need or type another one and press enter
          </p>
          <input
            aria-label="Custom file type"
            className="mt-3 rounded-lg border border-border-primary bg-bg-primary px-3.5 py-3 text-sm text-text-primary outline-hidden placeholder:text-text-placeholder"
            onChange={(event) => setCustomType(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addCustomType();
              }
            }}
            placeholder="e.g. GIF"
            value={customType}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            {typeOptions.map((type) => {
              const active = isTypeSelected(type);

              return (
                <button
                  aria-pressed={active}
                  className={twMerge(
                    "rounded-full border border-border-secondary bg-bg-secondary px-3 py-1 text-sm font-semibold text-text-secondary outline-hidden transition-colors hover:border-border-brand",
                    active &&
                      "border-utility-brand-200 bg-utility-brand-50 text-utility-brand-700",
                  )}
                  key={type}
                  onClick={() => toggleType(type)}
                  type="button"
                >
                  {type}
                </button>
              );
            })}
          </div>
        </div>

        <DialogFooter className="px-6 pb-6 pt-4">
          <Button
            className="flex-1"
            onPress={() => onOpenChange(false)}
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            className="flex-1"
            onPress={() => {
              onSave({ fileSizes: selected, fileTypes: selectedTypes });
              onOpenChange(false);
            }}
            variant="primary"
          >
            Save
          </Button>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
